/**
 * Admin / Host translation strings.
 *
 * Used via the `t()` helper from useLanguage():
 *   const { t } = useLanguage();
 *   <h1>{t('customers.title')}</h1>
 *
 * Structure: each top-level namespace has `en` and `ja` siblings.
 * Add new strings here rather than inlining `ja ? 'X' : 'Y'` ternaries
 * in admin pages.
 */

export const ADMIN_TRANSLATIONS = {
  // ── Common shared admin labels ───────────────────────────────────────────
  common: {
    en: {
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      close: 'Close',
      confirm: 'Confirm',
      back: 'Back',
      next: 'Next',
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      yes: 'Yes',
      no: 'No',
      search: 'Search',
      refresh: 'Refresh',
      export: 'Export',
      filter: 'Filter',
      all: 'All',
      none: 'None',
      retry: 'Retry',
      view: 'View',
      copy: 'Copy',
      copied: 'Copied',
      open: 'Open',
      apply: 'Apply',
      reset: 'Reset',
      logout: 'Logout',
      profile: 'Profile',
      notifications: 'Notifications',
      noResults: 'No results',
      somethingWentWrong: 'Something went wrong',
      unsavedChanges: 'You have unsaved changes',
      requiredField: 'This field is required',
    },
    ja: {
      save: '保存',
      cancel: 'キャンセル',
      delete: '削除',
      edit: '編集',
      close: '閉じる',
      confirm: '確認',
      back: '戻る',
      next: '次へ',
      loading: '読み込み中...',
      error: 'エラー',
      success: '成功',
      yes: 'はい',
      no: 'いいえ',
      search: '検索',
      refresh: '更新',
      export: 'エクスポート',
      filter: '絞り込み',
      all: 'すべて',
      none: 'なし',
      retry: '再試行',
      view: '表示',
      copy: 'コピー',
      copied: 'コピーしました',
      open: '開く',
      apply: '適用',
      reset: 'リセット',
      logout: 'ログアウト',
      profile: 'プロフィール',
      notifications: '通知',
      noResults: '結果がありません',
      somethingWentWrong: 'エラーが発生しました',
      unsavedChanges: '未保存の変更があります',
      requiredField: 'この項目は必須です',
    },
  },

  // ── Top-right dropdown / role-aware menu ─────────────────────────────────
  topMenu: {
    en: {
      customerDashboard: 'Dashboard',
      customerSettings: 'Settings',
      adminDashboard: 'Admin Dashboard',
      adminSettings: 'Admin Settings',
      hostTools: 'Host Tools',
      logout: 'Logout',
      switchLanguage: 'Switch Language',
    },
    ja: {
      customerDashboard: 'ダッシュボード',
      customerSettings: '設定',
      adminDashboard: '管理ダッシュボード',
      adminSettings: '管理設定',
      hostTools: 'ホスト管理ツール',
      logout: 'ログアウト',
      switchLanguage: '言語を切り替え',
    },
  },

  // ── Admin sidebar / navigation ───────────────────────────────────────────
  sidebar: {
    en: {
      overview: 'Overview',
      dashboard: 'Dashboard',
      customers: 'Customers',
      reports: 'Reports',
      reportSimulator: 'Report Simulator',
      security: 'Security Events',
      support: 'Support',
      templates: 'Templates',
      meisaiMonitor: 'ETC Usage Inquiry Service (MEISAI) Monitor',
      phaseBLive: 'ETC Usage Inquiry Service (MEISAI) Recovery Console',
      dataHealth: 'Data Health',
      launchChecklist: 'Launch Checklist',
      hostTools: 'Host Tools',
      adminSettings: 'Admin Settings',
      sectionOperations: 'Operations',
      sectionAccount: 'Account',
      sectionPlatform: 'Platform',
      sectionSafety: 'Safety',
    },
    ja: {
      overview: '概要',
      dashboard: 'ダッシュボード',
      customers: '顧客管理',
      reports: 'レポート',
      reportSimulator: 'レポートシミュレーター',
      security: 'セキュリティイベント',
      support: 'サポート',
      templates: 'テンプレート',
      meisaiMonitor: 'ETC利用照会サービス（MEISAI）モニター',
      phaseBLive: 'ETC利用照会サービス（MEISAI）回復コンソール',
      dataHealth: 'データ健全性',
      launchChecklist: 'ローンチ準備',
      hostTools: 'ホスト管理ツール',
      adminSettings: '管理設定',
      sectionOperations: '運用',
      sectionAccount: 'アカウント',
      sectionPlatform: 'プラットフォーム',
      sectionSafety: 'セーフティ',
    },
  },

  // ── Admin Settings page ──────────────────────────────────────────────────
  adminSettings: {
    en: {
      pageTitle: 'Admin Settings',
      pageSubtitle: 'Your preferences for the admin workspace',
      sectionLanguage: 'Language',
      languageHelp: 'Choose the language used across all admin pages while you are signed in.',
      languageSavedToast: 'Language preference saved',
      sectionProfile: 'Admin Profile',
      displayName: 'Display name',
      displayNameHelp: 'Shown on internal notes and audit history.',
      accountEmail: 'Account email',
      accountEmailReadOnly: 'Email cannot be changed here. Contact a host to update.',
      role: 'Role',
      sectionSecurity: 'Security',
      changePassword: 'Change password',
      currentPassword: 'Current password',
      newPassword: 'New password',
      confirmNewPassword: 'Confirm new password',
      passwordMismatch: 'New passwords do not match',
      passwordChanged: 'Password updated',
      sectionPreferences: 'Preferences',
      defaultLanding: 'Default landing page',
      defaultLandingHelp: 'Where you land after signing in.',
      timezone: 'Timezone',
      dateFormat: 'Date format',
      sectionNotifications: 'Notifications',
      emailOnNewCustomer: 'Email me when a new customer registers',
      emailOnSecurityEvent: 'Email me on important security events',
      saved: 'Saved',
    },
    ja: {
      pageTitle: '管理設定',
      pageSubtitle: '管理ワークスペースの設定',
      sectionLanguage: '言語',
      languageHelp: 'ログイン中、すべての管理ページで使用する言語を選択してください。',
      languageSavedToast: '言語設定を保存しました',
      sectionProfile: '管理者プロフィール',
      displayName: '表示名',
      displayNameHelp: '内部メモや監査履歴に表示されます。',
      accountEmail: 'アカウントメール',
      accountEmailReadOnly: 'こちらでは変更できません。変更にはホスト管理者へご連絡ください。',
      role: 'ロール',
      sectionSecurity: 'セキュリティ',
      changePassword: 'パスワード変更',
      currentPassword: '現在のパスワード',
      newPassword: '新しいパスワード',
      confirmNewPassword: '新しいパスワード（確認）',
      passwordMismatch: '新しいパスワードが一致しません',
      passwordChanged: 'パスワードを更新しました',
      sectionPreferences: '個人設定',
      defaultLanding: 'デフォルトの開始ページ',
      defaultLandingHelp: 'ログイン後に最初に表示されるページです。',
      timezone: 'タイムゾーン',
      dateFormat: '日付形式',
      sectionNotifications: '通知',
      emailOnNewCustomer: '新規顧客登録時にメール通知',
      emailOnSecurityEvent: '重要なセキュリティイベント発生時にメール通知',
      saved: '保存しました',
    },
  },

  // ── Customers list page ──────────────────────────────────────────────────
  customers: {
    en: {
      pageTitle: 'Customers',
      pageSubtitle: 'Manage all registered customer accounts',
      searchPlaceholder: 'Search by email, name, or customer ID...',
      newCustomer: 'New customer',
      bulkActions: 'Bulk actions',
      bulkResendVerification: 'Resend verification',
      bulkSuspend: 'Suspend selected',
      bulkDeleteUnverified: 'Delete unverified',
      bulkExport: 'Export selected',
      selectAll: 'Select all',
      countOfTotal: '{n} of {total}',
      colCustomerId: 'Customer ID',
      colCustomer: 'Customer',
      colJtrId: 'JTR ID',
      colRole: 'Role',
      colVerified: 'Verified',
      colMeisai: 'ETC Usage Inquiry Service (MEISAI)',
      colCreated: 'Created',
      colName: 'Name',
      colEmail: 'Email',
      colPlan: 'Plan',
      colStatus: 'Status',
      colLifecycle: 'Lifecycle',
      colRegistered: 'Registered',
      colLastActive: 'Last active',
      colActions: 'Actions',
      meisaiConnected: 'Connected',
      meisaiNotConnected: 'Not connected',
      loadingCustomers: 'Loading customers…',
      noMatch: 'No customers match "{q}"',
      hostToolsLink: 'Host Tools (wipe all customers, reset counter, backfill IDs) →',
      deleteModalTitle: 'Delete Customer Permanently?',
      deleteModalSubtitle: 'This action cannot be undone.',
      deleteModalEmailLabel: 'Email:',
      deleteModalJtrLabel: 'JTR ID:',
      deleteModalUnassigned: 'unassigned',
      deleteModalScopeLabel: 'Will delete:',
      deleteModalScopeBody: 'account + vault + ETC cards + Phase B runs + reports + email logs + audit notes.',
      deleteTypePromptPrefix: 'Type ',
      deleteTypePromptSuffix: ' to confirm:',
      deleteCancel: 'Cancel',
      deleteConfirmCta: 'Delete Permanently',
      deleting: 'Deleting…',
      titleSuspend: 'Suspend',
      titleReactivate: 'Reactivate',
      titleDelete: 'Delete',
      titleRestore: 'Restore',
      titleRefresh: 'Refresh',
      titleOpen360: 'Open Customer 360',
      // Filter pill tooltips
      filterAllTip: 'Every active (non-deleted) customer',
      filterVerifiedTip: 'Email verified',
      filterUnverifiedTip: 'Email not yet verified',
      filterLinkNotClickedTip: 'Verification email sent >24h ago, never clicked',
      filterVerifiedNotConnectedTip: 'Email verified but no ETC Usage Inquiry Service (MEISAI) vault yet',
      filterMeisaiConnectedTip: 'ETC Usage Inquiry Service (MEISAI) account validated',
      filterMeisaiUnverifiedTip: 'Vault exists but not validated',
      filterSuspendedTip: 'Manually suspended',
      filterBouncedTip: 'Email hard-bounced',
      filterNeedsReviewTip: 'Flagged for admin review',
      filterRecentTip: 'Registered in the last 7 days',
      filterDeletedTip: 'Soft-deleted accounts',
      filterVerifiedNotConnectedShort: 'Verified · No ETC Usage Inquiry Service (MEISAI)',
      filterMeisaiConnectedShort: 'ETC Usage Inquiry Service (MEISAI) Connected',
      filterMeisaiUnverifiedShort: 'ETC Usage Inquiry Service (MEISAI) Unverified',
      filterBouncedShort: 'Bounced',
      filterNeedsReviewShort: 'Needs Review',
      filterRecentShort: 'Recent (7d)',
      filterDeletedShort: 'Deleted',
      filterAllShort: 'All',
      filterVerifiedShort: 'Verified',
      filterUnverifiedShort: 'Unverified',
      filterLinkNotClickedShort: 'Link not clicked',
      filterSuspendedShort: 'Suspended',
      empty: 'No customers found',
      emptyHelp: 'Try clearing filters or adjusting your search.',
      loadError: 'Failed to load customers',
      view360: 'Open Customer 360',
      // Filter pill labels
      filterAll: 'All customers',
      filterVerified: 'Verified',
      filterUnverified: 'Unverified',
      filterLinkNotClicked: 'Link not clicked',
      filterVerifiedNotConnected: 'Verified — not connected',
      filterMeisaiConnected: 'ETC Usage Inquiry Service (MEISAI) connected',
      filterMeisaiUnverified: 'ETC Usage Inquiry Service (MEISAI) unverified',
      filterSuspended: 'Suspended',
      filterDeleted: 'Deleted',
      filterEmailBounced: 'Email bounced',
      filterEmailInvalid: 'Email invalid',
      filterNeedsReview: 'Needs review',
      filterRecentlyRegistered: 'Recently registered',
      filterSecurityRole: 'Security role',
      filterPlan: 'Plan / tier',
      // Confirmations
      confirmSuspendTitle: 'Suspend customer?',
      confirmSuspendBody: 'The customer will no longer be able to sign in until reactivated.',
      confirmDeleteTitle: 'Delete customer?',
      confirmDeleteBody: 'This is a soft delete and can be restored. Continue?',
      confirmHardDeleteTitle: 'Permanently delete customer?',
      confirmHardDeleteBody: 'This cannot be undone. Type DELETE to confirm.',
      confirmResendTitle: 'Resend verification?',
      confirmResendBody: 'A new verification email will be sent to this customer.',
      // Toasts
      toastSuspended: 'Customer suspended',
      toastReactivated: 'Customer reactivated',
      toastDeleted: 'Customer deleted',
      toastRestored: 'Customer restored',
      toastVerificationSent: 'Verification email sent',
      toastBulkApplied: 'Bulk action applied to {n} customer(s)',
    },
    ja: {
      pageTitle: '顧客管理',
      pageSubtitle: '登録済みの全顧客アカウントを管理',
      searchPlaceholder: 'メール、名前、顧客IDで検索...',
      newCustomer: '新規顧客',
      bulkActions: '一括操作',
      bulkResendVerification: '認証メール再送信',
      bulkSuspend: '選択した顧客を停止',
      bulkDeleteUnverified: '未認証顧客を削除',
      bulkExport: '選択をエクスポート',
      selectAll: 'すべて選択',
      countOfTotal: '{total}件中 {n}件',
      colCustomerId: '顧客ID',
      colCustomer: '顧客',
      colJtrId: 'JTR ID',
      colRole: '権限',
      colVerified: '認証',
      colMeisai: 'ETC Usage Inquiry Service (MEISAI)',
      colCreated: '登録日',
      colName: '名前',
      colEmail: 'メール',
      colPlan: 'プラン',
      colStatus: 'ステータス',
      colLifecycle: 'ライフサイクル',
      colRegistered: '登録日',
      colLastActive: '最終アクティブ',
      colActions: '操作',
      meisaiConnected: '連携済み',
      meisaiNotConnected: '未連携',
      loadingCustomers: '顧客を読み込み中…',
      noMatch: '「{q}」に一致する顧客はいません',
      hostToolsLink: 'ホストツール（顧客の全削除、カウンタリセット、ID再付与） →',
      deleteModalTitle: '顧客をアカウントを閉鎖するしますか？',
      deleteModalSubtitle: 'この操作は取り消せません。',
      deleteModalEmailLabel: 'メール：',
      deleteModalJtrLabel: 'JTR ID：',
      deleteModalUnassigned: '未割当',
      deleteModalScopeLabel: '削除対象：',
      deleteModalScopeBody: 'アカウント + Vault + ETCカード + Phase B 実行記録 + レポート + メールログ + 監査メモ。',
      deleteTypePromptPrefix: '確認のため ',
      deleteTypePromptSuffix: ' と入力してください：',
      deleteCancel: 'キャンセル',
      deleteConfirmCta: 'アカウントを閉鎖するする',
      deleting: '削除中…',
      titleSuspend: '停止',
      titleReactivate: '再有効化',
      titleDelete: '削除',
      titleRestore: '復元',
      titleRefresh: '更新',
      titleOpen360: '顧客360を開く',
      filterAllTip: '削除されていない全てのアクティブな顧客',
      filterVerifiedTip: 'メール認証済み',
      filterUnverifiedTip: 'メール未認証',
      filterLinkNotClickedTip: '認証メール送信から24時間以上、未クリック',
      filterVerifiedNotConnectedTip: 'メール認証済みだがETC利用照会サービス（MEISAI） Vault未作成',
      filterMeisaiConnectedTip: 'ETC利用照会サービス（MEISAI）アカウント検証済み',
      filterMeisaiUnverifiedTip: 'Vault作成済みだが未検証',
      filterSuspendedTip: '手動で停止された顧客',
      filterBouncedTip: 'メールがハードバウンス',
      filterNeedsReviewTip: '管理者の確認が必要',
      filterRecentTip: '直近7日以内の登録',
      filterDeletedTip: 'ソフト削除済みのアカウント',
      filterVerifiedNotConnectedShort: '認証済み・未連携',
      filterMeisaiConnectedShort: 'ETC利用照会サービス（MEISAI）アカウント接続済み',
      filterMeisaiUnverifiedShort: 'ETC利用照会サービス（MEISAI）未確認',
      filterBouncedShort: 'バウンス',
      filterNeedsReviewShort: '要確認',
      filterRecentShort: '最近(7日)',
      filterDeletedShort: '削除済み',
      filterAllShort: '全て',
      filterVerifiedShort: '認証済み',
      filterUnverifiedShort: '未認証',
      filterLinkNotClickedShort: 'リンク未クリック',
      filterSuspendedShort: '停止中',
      empty: '該当する顧客がいません',
      emptyHelp: '絞り込み条件をクリアするか、検索キーワードを変更してください。',
      loadError: '顧客一覧の読み込みに失敗しました',
      view360: '顧客360を開く',
      filterAll: 'すべての顧客',
      filterVerified: '認証済み',
      filterUnverified: '未認証',
      filterLinkNotClicked: 'リンク未クリック',
      filterVerifiedNotConnected: '認証済み・未連携',
      filterMeisaiConnected: 'ETC利用照会サービス（MEISAI）アカウント接続済み',
      filterMeisaiUnverified: 'ETC利用照会サービス（MEISAI）未確認',
      filterSuspended: '停止中',
      filterDeleted: '削除済み',
      filterEmailBounced: 'メール返送',
      filterEmailInvalid: 'メール無効',
      filterNeedsReview: '要確認',
      filterRecentlyRegistered: '最近登録',
      filterSecurityRole: 'セキュリティロール',
      filterPlan: 'プラン / 階層',
      confirmSuspendTitle: '顧客を停止しますか？',
      confirmSuspendBody: '再有効化されるまで、この顧客はログインできなくなります。',
      confirmDeleteTitle: '顧客を削除しますか？',
      confirmDeleteBody: 'これはソフト削除で、復元が可能です。続行しますか？',
      confirmHardDeleteTitle: '顧客をアカウントを閉鎖するしますか？',
      confirmHardDeleteBody: 'この操作は取り消せません。確認のため DELETE と入力してください。',
      confirmResendTitle: '認証メールを再送信しますか？',
      confirmResendBody: 'この顧客に新しい認証メールが送信されます。',
      toastSuspended: '顧客を停止しました',
      toastReactivated: '顧客を再有効化しました',
      toastDeleted: '顧客を削除しました',
      toastRestored: '顧客を復元しました',
      toastVerificationSent: '認証メールを送信しました',
      toastBulkApplied: '{n}件の顧客に一括操作を適用しました',
    },
  },

  // ── Lifecycle / status badges (used everywhere) ──────────────────────────
  lifecycle: {
    en: {
      pendingEmail: 'Pending Email',
      pendingMeisai: 'Pending ETC Usage Inquiry Service (MEISAI)',
      setupDone: 'Setup Done',
      trialActive: 'Trial Active',
      freeWeekly: 'Free Weekly',
      paid: 'Paid',
      suspended: 'Suspended',
      deleted: 'Deleted',
      needsReview: 'Needs Review',
      verified: 'Verified',
      unverified: 'Unverified',
      meisaiConnected: 'ETC Usage Inquiry Service (MEISAI) Connected',
      meisaiUnverified: 'ETC Usage Inquiry Service (MEISAI) Unverified',
      active: 'Active',
      inactive: 'Inactive',
      pending: 'Pending',
      bounced: 'Bounced',
      invalid: 'Invalid',
    },
    ja: {
      pendingEmail: 'メール認証待ち',
      pendingMeisai: 'ETC利用照会サービス（MEISAI）待ち',
      setupDone: 'セットアップ完了',
      trialActive: 'トライアル中',
      freeWeekly: '無料週次',
      paid: '有料',
      suspended: '停止中',
      deleted: '削除済み',
      needsReview: '要確認',
      verified: '認証済み',
      unverified: '未認証',
      meisaiConnected: 'ETC利用照会サービス（MEISAI）アカウント接続済み',
      meisaiUnverified: 'ETC利用照会サービス（MEISAI）未確認',
      active: '有効',
      inactive: '無効',
      pending: '保留中',
      bounced: '返送',
      invalid: '無効',
    },
  },

  // ── Plan / tier ──────────────────────────────────────────────────────────
  plan: {
    en: {
      freeWeekly: 'Free Weekly',
      premiumTrial: 'Premium Trial',
      dailyPlusMonthly: 'Daily Plus Monthly',
      dailyPlusAnnual: 'Daily Plus Annual',
      founderPass: 'Early Access Pass',
      none: 'No plan',
    },
    ja: {
      freeWeekly: '無料週次',
      premiumTrial: 'プレミアム体験',
      dailyPlusMonthly: 'Daily Plus 月額',
      dailyPlusAnnual: 'Daily Plus 年額',
      founderPass: '先行利用パス',
      none: 'プランなし',
    },
  },

  // ── Security role labels ────────────────────────────────────────────────
  role: {
    en: {
      host: 'Host',
      admin: 'Admin',
      customer: 'Customer',
      viewer: 'Viewer',
    },
    ja: {
      host: 'ホスト',
      admin: '管理者',
      customer: '顧客',
      viewer: '閲覧者',
    },
  },

  // ── Launch Readiness Checklist page ──────────────────────────────────────
  launchChecklist: {
    en: {
      title: 'Launch Readiness Checklist',
      subtitle: 'Complete all items before enabling production launch',
      saveProgress: 'Save Progress',
      saving: '...',
      saved: 'Checklist saved',
      saveError: 'Error saving',
      allComplete: 'All pre-launch items are complete. The service is ready for production review.',
      footerWarning: 'Do not enable automation until production credential handling, connection testing, and customer authorization workflows are finalized.',
      cat: {
        Content: 'Content', Legal: 'Legal', Infrastructure: 'Infrastructure',
        Payments: 'Payments', Automation: 'Automation', QA: 'QA',
      },
      items: {
        japanese_copy_reviewed: 'Japanese copy reviewed by native speaker',
        legal_documents_reviewed: 'Terms, Privacy, Disclaimer reviewed',
        mandrill_connected: 'Mandrill email service connected (EMAIL_MODE=mandrill)',
        stripe_live_price_id: 'Stripe live Price ID configured (STRIPE_PRICE_ID_DAILY_PLUS)',
        stripe_webhook_tested: 'Stripe webhook endpoint tested with live events',
        email_templates_reviewed: 'Email templates reviewed for JA/EN content',
        dns_configured: 'DNS configured for japantollreceipts.jp',
        domain_ssl_verified: 'SSL certificate verified for domain',
        com_redirect_configured: '.com domain redirect to .jp configured',
        playwright_compliance_approved: 'Automation compliance and customer authorization workflows approved',
        playwright_enabled_confirmed: 'Automation flag reviewed and set appropriately',
        test_customer_flow: 'Full customer registration flow tested end-to-end',
        test_paid_upgrade: 'Paid plan upgrade flow tested with Stripe',
        test_report_email: 'Report email delivery tested',
        data_deletion_flow_tested: 'Account deactivation and data deletion tested',
      },
    },
    ja: {
      title: 'ローンチ準備チェックリスト',
      subtitle: '本番ローンチを有効にする前に、すべての項目を完了してください',
      saveProgress: '進捗を保存',
      saving: '...',
      saved: 'チェックリストを保存しました',
      saveError: '保存に失敗しました',
      allComplete: 'ローンチ前の項目がすべて完了しました。本番レビューの準備が整っています。',
      footerWarning: '本番の認証情報の取り扱い、接続テスト、お客様の利用許諾フローが確定するまで、自動化を有効にしないでください。',
      cat: {
        Content: 'コンテンツ', Legal: '法務', Infrastructure: 'インフラ',
        Payments: '決済', Automation: '自動化', QA: 'QA',
      },
      items: {
        japanese_copy_reviewed: 'ネイティブによる日本語コピーの確認',
        legal_documents_reviewed: '利用規約・プライバシー・免責事項の確認',
        mandrill_connected: 'Mandrillメール配信サービスの接続（EMAIL_MODE=mandrill）',
        stripe_live_price_id: 'Stripe本番Price IDの設定（STRIPE_PRICE_ID_DAILY_PLUS）',
        stripe_webhook_tested: 'Stripe Webhookエンドポイントの本番イベント検証',
        email_templates_reviewed: 'メールテンプレートのJA/EN内容確認',
        dns_configured: 'japantollreceipts.jp のDNS設定',
        domain_ssl_verified: 'ドメインのSSL証明書の確認',
        com_redirect_configured: '.comドメインから.jpへのリダイレクト設定',
        playwright_compliance_approved: '自動化コンプライアンスとお客様の利用許諾フローの承認',
        playwright_enabled_confirmed: '自動化フラグの確認と適切な設定',
        test_customer_flow: '顧客登録フロー全体のエンドツーエンドテスト',
        test_paid_upgrade: 'Stripeでの有料プランアップグレードフローのテスト',
        test_report_email: 'レポートメール配信のテスト',
        data_deletion_flow_tested: 'アカウント無効化とデータ削除のテスト',
      },
    },
  },
};

/**
 * Resolve a dotted translation key like "customers.filterAll" against a lang.
 * Returns the key itself if not found (so missing keys are obvious in dev).
 */
export function resolveTranslation(key, lang) {
  if (!key) return '';
  const parts = key.split('.');
  if (parts.length < 2) return key;
  const ns = parts[0];
  const path = parts.slice(1);
  const root = ADMIN_TRANSLATIONS[ns];
  if (!root) return key;
  const dict = root[lang] || root.en || {};
  let cur = dict;
  for (const p of path) {
    if (cur && typeof cur === 'object' && p in cur) cur = cur[p];
    else return key;
  }
  return typeof cur === 'string' ? cur : key;
}

/**
 * Apply simple {placeholder} interpolation to a translated string.
 */
export function interpolate(str, vars) {
  if (!str || !vars) return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : `{${k}}`));
}
